import { useQueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  useExternalServiceAssignee,
  useFeatureflags,
  useOrderReports,
  useOrdersCache,
} from "@web-app/api";
import { Button, Header, Tabs } from "@web-app/components";
import useDispatchStore, {
  OrderDetailsPanelTab,
} from "@web-app/store/dispatchStore";
import { BaseApp, Order, SystemApp, User } from "@web-app/types";

import OrderSummaryTab from "../OrderSummaryTab";

import DoordashDeliveryInfoV2 from "./DoordashDeliveryInfoV2";
import DriverRoute from "./DriverRoute";
import EZCaterDeliveryInfo from "./EZCaterDeliveryInfo";
import ExpediteDeliveryInfo from "./ExpediteDeliveryInfo";
import OnfleetDeliveryInfo from "./OnfleetDeliveryInfo";
import OrderAttachmentsTab from "./OrderAttachmentsTab";
import OrderChatHistoryTab from "./OrderChatHistoryTab";
import OrderTimelineTab from "./OrderTimelineTab";
import RoadieDeliveryInfo from "./RoadieDeliveryInfo";
import UberDeliveryInfoV2 from "./UberDeliveryInfoV2";

interface OrderDetailsPanelProps {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  order: Order;
}

const OrderDetailsPanel = memo(function OrderDetailsPanel({
  appsMap,
  assigneesMap,
  order,
}: OrderDetailsPanelProps) {
  const [externalProvider, setExternalProvider] = useState<
    "" | "doordash" | "uber-catering" | "uber"
  >("");
  const orderDetailsPanelInitialActiveTab = useDispatchStore(
    (state) => state.orderDetailsPanelInitialActiveTab,
  );
  const reports = useOrderReports(order.id);
  const { hasFeature } = useFeatureflags();
  const { invalidateOrdersCache } = useOrdersCache();
  const queryClient = useQueryClient();

  const { data: externalServiceAssignee } = useExternalServiceAssignee(
    "order",
    order.id,
  );

  useEffect(() => {
    let deliveryOption: "" | "doordash" | "uber-catering" | "uber" = "";
    if (order?.externalAssigneeId?.startsWith("uber-catering")) {
      deliveryOption = "uber-catering";
    } else if (order?.externalAssigneeId?.startsWith("uber")) {
      deliveryOption = "uber";
    } else if (order?.externalAssigneeId?.startsWith("doordash")) {
      deliveryOption = "doordash";
    }

    if (order) {
      setExternalProvider(deliveryOption);
    }
  }, [order, order?.externalAssigneeId]);

  const tabs = useMemo(() => {
    const baseTabs: {
      content: React.ReactNode;
      id: string;
      title: React.ReactNode;
    }[] = [
      {
        content: (
          <OrderSummaryTab
            order={order}
            assignee={
              order.assigneeId ? assigneesMap.get(order.assigneeId) : null
            }
          />
        ),
        id: "summary",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Summary</span>
          </div>
        ),
      },
      {
        content: <OrderTimelineTab order={order} />,
        id: "timeline",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Timeline</span>
            {reports.length > 0 && (
              <span className="h-[17px] w-[20px] inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold bg-red-700 text-white rounded-[40px]">
                {reports.length}
              </span>
            )}
          </div>
        ),
      },
      {
        content: <OrderAttachmentsTab order={order} />,
        id: "attachments",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Attachments</span>
          </div>
        ),
      },
    ];

    if (hasFeature("conversations")) {
      baseTabs.push({
        content: <OrderChatHistoryTab order={order} />,
        id: "chat-history",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Chat History</span>
          </div>
        ),
      });
    }

    if (externalProvider === "uber" || externalProvider === "uber-catering") {
      baseTabs.push({
        content: (
          <UberDeliveryInfoV2
            externalAssigneeId={order.externalAssigneeId || ""}
            entity={"order"}
            entityId={order.id}
            timezone={order.timezone || "system"}
          />
        ),
        id: "uber",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Uber</span>
          </div>
        ),
      });
    } else if (externalServiceAssignee?.type === "doordash") {
      baseTabs.push({
        content: (
          <DoordashDeliveryInfoV2
            orderId={order.id}
            timezone={order.timezone || "system"}
          />
        ),
        id: "doordash",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Doordash</span>
          </div>
        ),
      });
    } else if (externalServiceAssignee?.type === "roadie") {
      baseTabs.push({
        content: (
          <RoadieDeliveryInfo
            entity="order"
            entityId={order.id}
            timezone={order.timezone || "system"}
          />
        ),
        id: "roadie",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Roadie</span>
          </div>
        ),
      });
    } else if (
      externalServiceAssignee?.type === "expedite" &&
      externalServiceAssignee.remark === "accepted"
    ) {
      baseTabs.push({
        content: (
          <ExpediteDeliveryInfo
            entity="order"
            entityId={order.id}
            timezone={order.timezone || "system"}
          />
        ),
        id: "expedite",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Expedite</span>
          </div>
        ),
      });
    } else if (externalServiceAssignee?.type === "onfleet") {
      baseTabs.push({
        content: (
          <OnfleetDeliveryInfo
            entity="order"
            entityId={order.id}
            onCancelSuccess={() => {
              invalidateOrdersCache();
              queryClient.invalidateQueries({
                queryKey: ["externalServiceAssignee", "order", order.id],
              });
              setActiveTabIndex(1);
            }}
          />
        ),
        id: "onfleet",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Onfleet</span>
          </div>
        ),
      });
    }

    // Add EZCater tab if order has EZCater app
    const orderApp = order.appId ? appsMap.get(order.appId) : null;
    if (orderApp?.systemApp === SystemApp.EZCATER) {
      baseTabs.push({
        content: (
          <EZCaterDeliveryInfo
            orderId={order.id}
            timezone={order.timezone || "system"}
          />
        ),
        id: "ezcater",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>EZCater</span>
          </div>
        ),
      });
    }

    if (order.driverRouteDistance && order.driverRouteDistance > 0) {
      baseTabs.push({
        content: <DriverRoute order={order} />,
        id: "driver-tracking",
        title: (
          <div className="tw-sidebar-tabs-style">
            <span>Driver Tracking</span>
          </div>
        ),
      });
    }

    return baseTabs;
  }, [
    order,
    appsMap,
    assigneesMap,
    externalProvider,
    reports.length,
    externalServiceAssignee,
    hasFeature,
    invalidateOrdersCache,
    queryClient,
  ]);

  const getTabIndexFromId = useCallback(
    (tabId: OrderDetailsPanelTab): number => {
      const index = tabs.findIndex((tab) => tab.id === tabId);
      return index >= 0 ? index : 0; // Default to 0 (summary) if tab not found
    },
    [tabs],
  );

  const [activeTabIndex, setActiveTabIndex] = useState(() =>
    getTabIndexFromId(orderDetailsPanelInitialActiveTab),
  );

  const onChangeTab = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
    const tabId: OrderDetailsPanelTab | undefined = tabs[tabIndex]
      ?.id as OrderDetailsPanelTab;
    if (!tabId) return;

    useDispatchStore.setState({ orderDetailsPanelInitialActiveTab: tabId });
  };

  useEffect(() => {
    // Reset active tab to summary when component unmounts
    return () => {
      useDispatchStore.setState({
        orderDetailsPanelInitialActiveTab: "summary",
      });
      setActiveTabIndex(0);
    };
  }, []);

  const handleClose = () => {
    useDispatchStore.setState({
      focusedOrderId: null,
      mapFocusedOrderId: null,
      orderDetailsPanelInitialActiveTab: "summary",
      selectedSidebarOrderIds: [],
      selectedSidebarRouteIds: [],
    });
  };

  if (!order) {
    return null;
  }

  return (
    <div className="absolute bg-white h-[100dvh] lg:h-full w-full z-30 flex flex-col overflow-hidden">
      <Header
        title={order.displayId}
        borderLess
        leftActions={
          <Button
            icon="fasChevronLeft"
            onClick={handleClose}
            plain
            className="!p-0 hover:!bg-white"
            iconClassName="!h-3 lg:!h-4 !w-3 lg:!w-4"
          />
        }
        rightActions={
          <Button
            type="button"
            size="sm"
            color="white"
            onClick={() => {
              useDispatchStore.setState({
                isEditOrder: true,
              });
            }}
            className="!p-0"
            icon="fasPencil"
            iconClassName="!h-3 !w-3 hover:bg-gray-100 transition-all border border-gray-300 p-[5px] lg:p-2 rounded-md"
          />
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto bg-white pb-5 lg:pb-0">
        <Tabs
          tabs={tabs}
          selectedIndex={activeTabIndex}
          onChange={onChangeTab}
        />
      </div>
    </div>
  );
});

export default OrderDetailsPanel;
